import React, { useEffect, useState, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const DEFAULT_SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Tamil',
  'Computer Science', 'Physics', 'Chemistry', 'Biology', 'History',
  'Geography', 'Economics', 'Commerce', 'General',
];
const TASK_TYPES  = ['Homework', 'Assignment', 'Project', 'Class Test', 'Activity', 'Reading', 'Practice', 'Other'];
const SECTIONS    = ['A', 'B', 'C', 'D', 'E', 'All'];
const PRIORITIES  = ['Normal', 'Important', 'Urgent'];

const TASK_TYPE_COLORS = {
  Homework: '#6366f1', Assignment: '#0ea5e9', Project: '#8b5cf6',
  'Class Test': '#ef4444', Activity: '#22c55e', Reading: '#f59e0b',
  Practice: '#64748b', Other: '#94a3b8',
};

const blank = {
  title: '', description: '', task_type: 'Homework', class_grade: '', section: 'All',
  subject: '', due_date: '', due_time: '', max_marks: '', priority: 'Normal',
  instructions: '', resources_link: '',
};

export default function StudentCommunication({ moduleKey = 'student_communication' }) {
  const { user }  = useAuth();
  const isAdmin   = ['admin', 'manager', 'teacher', 'faculty', 'principal'].includes(user?.roleKey);

  const [tasks,        setTasks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [showForm,     setShowForm]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(blank);
  const [saving,       setSaving]       = useState(false);
  const [activeTab,    setActiveTab]    = useState('active');
  const [filterClass,  setFilterClass]  = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [filterSubject,setFilterSubject]= useState('');
  const [expandedId,   setExpandedId]   = useState(null);
  const [toast,        setToast]        = useState({ show: false, message: '', type: 'success' });
  const [classOptions, setClassOptions] = useState(DEFAULT_CLASSES);
  const [subjectOpts,  setSubjectOpts]  = useState(DEFAULT_SUBJECTS);

  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec) {
          if (rec.classes?.length)  setClassOptions(rec.classes);
          if (rec.subjects?.length) setSubjectOpts(rec.subjects);
        }
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get(`/records/${moduleKey}`, { params: { limit: 100 } })
      .catch(() => ({ data: { records: [] } }));
    let items = r.data.records || [];
    items.sort((a, b) => new Date(a.recordData?.due_date || '9999') - new Date(b.recordData?.due_date || '9999'));
    setTasks(items);
    setLoading(false);
  }, [moduleKey]);

  useEffect(() => { load(); }, [load]);

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing?._id) await api.put(`/records/${moduleKey}/${editing._id}`, form);
      else              await api.post(`/records/${moduleKey}`, form);
      setToast({ show: true, message: `Task ${editing ? 'updated' : 'created'}!`, type: 'success' });
      setShowForm(false); setEditing(null); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await api.delete(`/records/${moduleKey}/${id}`).catch(() => {});
    load();
  };

  const markComplete = async (task) => {
    const d = task.recordData || {};
    await api.put(`/records/${moduleKey}/${task._id}`, { ...d, status: 'Completed' }).catch(() => {});
    load();
  };

  const openEdit = (r) => {
    setForm({ ...blank, ...r.recordData });
    setEditing(r); setShowForm(true);
  };

  const openNew = () => { setForm(blank); setEditing(null); setShowForm(true); };

  // Filtering
  const now = new Date();
  const tabFilter = {
    active:    t => !['Completed', 'Cancelled'].includes(t.recordData?.status) && (!t.recordData?.due_date || new Date(t.recordData.due_date) >= now),
    overdue:   t => !['Completed', 'Cancelled'].includes(t.recordData?.status) && t.recordData?.due_date && new Date(t.recordData.due_date) < now,
    completed: t => t.recordData?.status === 'Completed',
    all:       () => true,
  };

  const filtered = tasks.filter(t =>
    (tabFilter[activeTab]?.(t) !== false) &&
    (!filterClass   || t.recordData?.class_grade === filterClass) &&
    (!filterType    || t.recordData?.task_type   === filterType) &&
    (!filterSubject || t.recordData?.subject      === filterSubject)
  );

  const counts = {
    active:    tasks.filter(tabFilter.active).length,
    overdue:   tasks.filter(tabFilter.overdue).length,
    completed: tasks.filter(tabFilter.completed).length,
  };

  return (
    <div>
      <PageHeader
        title="Student Tasks & Assignments"
        icon="bi-list-task"
        subtitle="Assign homework, projects and tasks to students by class"
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width: 155 }}
              value={filterClass} onChange={e => setFilterClass(e.target.value)}>
              <option value="">All Classes</option>
              {classOptions.map(c => <option key={c}>{c}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 130 }}
              value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {TASK_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 130 }}
              value={filterSubject} onChange={e => setFilterSubject(e.target.value)}>
              <option value="">All Subjects</option>
              {subjectOpts.map(s => <option key={s}>{s}</option>)}
            </select>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                <i className="bi bi-plus-lg me-1"></i>New Task
              </button>
            )}
          </div>
        }
      />

      {/* Summary cards */}
      <div className="row g-2 mb-3">
        {[
          { label: 'Active Tasks', count: counts.active,    color: '#6366f1', bg: '#ede9fe', icon: 'bi-list-task' },
          { label: 'Overdue',      count: counts.overdue,   color: '#ef4444', bg: '#fee2e2', icon: 'bi-exclamation-circle-fill' },
          { label: 'Completed',    count: counts.completed, color: '#198754', bg: '#d1fae5', icon: 'bi-check-circle-fill' },
          { label: 'Total',        count: tasks.length,     color: '#0ea5e9', bg: '#e0f2fe', icon: 'bi-stack' },
        ].map(s => (
          <div className="col-6 col-md-3" key={s.label}>
            <div className="card text-center py-2" style={{ border: `1px solid ${s.bg}`, background: s.bg }}>
              <div className="d-flex align-items-center justify-content-center gap-2">
                <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: 18 }}></i>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.count}</div>
                  <div style={{ fontSize: 11, color: s.color, fontWeight: 600 }}>{s.label}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'active',    label: 'Active',    icon: 'bi-list-task' },
          { key: 'overdue',   label: 'Overdue',   icon: 'bi-exclamation-circle' },
          { key: 'completed', label: 'Completed', icon: 'bi-check-circle' },
          { key: 'all',       label: 'All Tasks', icon: 'bi-stack' },
        ].map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link ${activeTab === t.key ? 'active fw-semibold' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.key !== 'all' && counts[t.key] > 0 && (
                <span className={`badge ms-1 ${t.key === 'overdue' ? 'bg-danger' : 'bg-secondary'}`}
                  style={{ fontSize: 9 }}>{counts[t.key]}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      {/* Compose form */}
      {showForm && (
        <div className="card mb-4" style={{ border: '2px solid #c7d2fe', borderRadius: 12 }}>
          <div className="card-header d-flex justify-content-between align-items-center"
            style={{ background: '#f5f3ff' }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              <i className="bi bi-list-task me-2 text-primary"></i>
              {editing ? 'Edit Task / Assignment' : 'New Task / Assignment'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label fw-semibold">Task Title <span className="text-danger">*</span></label>
                  <input className="form-control" required value={form.title}
                    onChange={e => sf('title')(e.target.value)}
                    placeholder="e.g. Chapter 5 Worksheet" />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Task Type</label>
                  <select className="form-select" value={form.task_type}
                    onChange={e => sf('task_type')(e.target.value)}>
                    {TASK_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Priority</label>
                  <select className="form-select" value={form.priority}
                    onChange={e => sf('priority')(e.target.value)}>
                    {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold">Class / Grade <span className="text-danger">*</span></label>
                  <select className="form-select" required value={form.class_grade}
                    onChange={e => sf('class_grade')(e.target.value)}>
                    <option value="">Select class</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Section</label>
                  <select className="form-select" value={form.section}
                    onChange={e => sf('section')(e.target.value)}>
                    {SECTIONS.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Subject <span className="text-danger">*</span></label>
                  <select className="form-select" required value={form.subject}
                    onChange={e => sf('subject')(e.target.value)}>
                    <option value="">Select subject</option>
                    {subjectOpts.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Due Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" required
                    value={form.due_date} onChange={e => sf('due_date')(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Due Time</label>
                  <input type="time" className="form-control"
                    value={form.due_time || ''} onChange={e => sf('due_time')(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label fw-semibold">Max Marks</label>
                  <input type="number" className="form-control" min={1}
                    value={form.max_marks || ''} onChange={e => sf('max_marks')(e.target.value)} />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Description / Task Details <span className="text-danger">*</span></label>
                  <textarea className="form-control" rows={4} required
                    value={form.description} onChange={e => sf('description')(e.target.value)}
                    placeholder="Describe the task, what students need to do…" />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Instructions / Guidelines</label>
                  <textarea className="form-control" rows={2}
                    value={form.instructions || ''} onChange={e => sf('instructions')(e.target.value)}
                    placeholder="Submission format, word limits, etc." />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Resource Link <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>(optional)</span></label>
                  <input type="url" className="form-control"
                    placeholder="https://…"
                    value={form.resources_link || ''} onChange={e => sf('resources_link')(e.target.value)} />
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3 pt-2 border-top">
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Task' : <><i className="bi bi-send me-1"></i>Assign Task</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task list */}
      {loading ? (
        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-5 text-muted">
          <i className="bi bi-list-task display-4 d-block mb-2 opacity-25"></i>
          No tasks found.
          {isAdmin && (
            <> <button className="btn btn-link p-0" onClick={openNew}>Create the first task</button></>
          )}
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {filtered.map(t => {
            const d         = t.recordData || {};
            const isExpanded = expandedId === t._id;
            const typeColor  = TASK_TYPE_COLORS[d.task_type] || '#6366f1';
            const isDue      = d.due_date && new Date(d.due_date) < now && d.status !== 'Completed';
            const dueStr     = d.due_date
              ? new Date(d.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
              : null;

            return (
              <div key={t._id} className="card"
                style={{ borderRadius: 12, borderLeft: `4px solid ${typeColor}`, background: isDue ? '#fffbf5' : '#fff' }}>
                <div className="card-body">
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span style={{ fontWeight: 700, fontSize: 15 }}>{d.title}</span>
                        <span className="badge"
                          style={{ background: typeColor + '20', color: typeColor, fontSize: 10 }}>
                          {d.task_type}
                        </span>
                        {d.subject && (
                          <span className="badge bg-light text-dark" style={{ fontSize: 10 }}>
                            <i className="bi bi-book me-1"></i>{d.subject}
                          </span>
                        )}
                        {d.class_grade && (
                          <span className="badge bg-light text-dark" style={{ fontSize: 10 }}>
                            {d.class_grade}{d.section && d.section !== 'All' ? ` - ${d.section}` : ''}
                          </span>
                        )}
                        {d.max_marks && (
                          <span className="badge" style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 10 }}>
                            {d.max_marks} marks
                          </span>
                        )}
                        {d.status === 'Completed' && (
                          <span className="badge bg-success" style={{ fontSize: 10 }}>
                            <i className="bi bi-check2 me-1"></i>Completed
                          </span>
                        )}
                        {isDue && (
                          <span className="badge bg-danger" style={{ fontSize: 10 }}>
                            <i className="bi bi-exclamation-circle me-1"></i>Overdue
                          </span>
                        )}
                      </div>
                      <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 11, color: '#94a3b8' }}>
                        {dueStr && (
                          <span style={{ color: isDue ? '#ef4444' : '#64748b' }}>
                            <i className="bi bi-clock me-1"></i>Due: {dueStr}
                            {d.due_time && ` at ${d.due_time}`}
                          </span>
                        )}
                        <span>
                          <i className="bi bi-calendar me-1"></i>
                          Assigned {new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0 flex-wrap">
                      <button className="btn btn-outline-secondary btn-sm"
                        style={{ fontSize: 11, padding: '2px 8px' }}
                        onClick={() => setExpandedId(isExpanded ? null : t._id)}>
                        {isExpanded ? 'Collapse' : 'View'}
                      </button>
                      {isAdmin && d.status !== 'Completed' && (
                        <button className="btn btn-outline-success btn-sm"
                          style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => markComplete(t)}>
                          <i className="bi bi-check2"></i>
                        </button>
                      )}
                      {isAdmin && (
                        <>
                          <button className="btn btn-outline-primary btn-sm"
                            style={{ padding: '2px 6px' }} onClick={() => openEdit(t)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-outline-danger btn-sm"
                            style={{ padding: '2px 6px' }} onClick={() => handleDelete(t._id)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {!isExpanded && d.description && (
                    <div style={{
                      fontSize: 13, color: '#64748b', marginTop: 8,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {d.description}
                    </div>
                  )}

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-top">
                      <div style={{ fontSize: 14, lineHeight: 1.8, whiteSpace: 'pre-wrap', color: '#334155' }}>
                        {d.description}
                      </div>
                      {d.instructions && (
                        <div className="mt-3 p-3 rounded"
                          style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: 13 }}>
                          <strong style={{ color: '#166534' }}>
                            <i className="bi bi-info-circle me-1"></i>Instructions:
                          </strong>
                          <div className="mt-1" style={{ whiteSpace: 'pre-wrap', color: '#166534' }}>
                            {d.instructions}
                          </div>
                        </div>
                      )}
                      {d.resources_link && (
                        <div className="mt-2">
                          <a href={d.resources_link} target="_blank" rel="noreferrer"
                            className="btn btn-outline-primary btn-sm">
                            <i className="bi bi-box-arrow-up-right me-1"></i>Open Resource
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
